import React from 'react';

import { useQueryMobileAppSubmissionDetailsHook } from '@fd/customHooks/useQueryMobileAppsSubmissionDetailsHook';
import { useQueryMobileAppStatisticsHook } from '@fd/customHooks/useQueryMobileAppStatisticsHook';
import { MobileAppsSubmissionStatus } from '@flipdish/api-client-typescript';
import { Grid, makeStyles, Theme } from '@material-ui/core';
import PhoneIphoneIcon from '@material-ui/icons/PhoneIphone';
import { getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import Permissions from 'react-redux-permissions';

import { DashboardCard, Typography } from '@fd/ui/atoms';
import PageLayout from '@fd/ui/Layout';
import { PageHeader } from '@fd/ui/molecules';
import { FailedResponseMessage } from '@fd/ui/molecules/FailedResponseMessage';

import { notifyError, NotifyProps } from '../../../../../layouts/Notify/actions';
import { permissionsSelector } from '../../../../../selectors/permissions.selector';
import { flagService } from '../../../../../services';
import { AppDetails } from '../../components/AppDetails';
import { AppDownloadsCard } from '../../components/AppDownloadsCard';
import { Discovery } from '../../components/Discovery';
import { GeneralSettings } from '../../components/GeneralSettings';
import { LegacyMobileSalesChannelsConfig } from '../../components/LegacyMobileSalesChannelsConfig';
import { PlatformSettingCard } from '../../components/PlatformSettingCard';

const useStyles = makeStyles((theme: Theme) => ({
  brandedAppsIcon: {
    color: theme.palette.grey[700],
    height: '108.67px',
    width: '64.67px',
  },
  container: {
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
    },
  },
  gridItem: {
    display: 'flex',
  },
  revenueTextContainer: {
    maxWidth: '350px',
  },
  revenueText: {
    fontSize: '1.5rem',
    fontWeight: 500,
    lineHeight: '2rem',
  },
  sectionContainer: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
  },
}));

const MobileApps = (props: MappedState & MappedDispatch) => {
  const {
    appId,
    hasEditSalesChannelsPermission,
    isFlipdishStaff,
    isMobileAppsOn,
    hasAndroidApp,
    hasIosApp,
    notifyError,
    translate,
  } = props;
  const classes = useStyles();

  const getSubmissionDetailsQuery = useQueryMobileAppSubmissionDetailsHook(
    appId,
    isMobileAppsOn || false
  );

  const hasPublishedApp =
    getSubmissionDetailsQuery.data?.Data.Status?.findIndex(
      (status) => status.AppStatus === MobileAppsSubmissionStatus.AppStatusEnum.Published
    ) !== -1;

  const mobileAppStatisticsQuery = useQueryMobileAppStatisticsHook(
    appId,
    ['Android'],
    (isMobileAppsOn && hasPublishedApp) || false
  );

  const handleDisplayError = () => {
    notifyError({ message: 'Error_please_try_again_later', translate: true });
  };

  const renderOverview = () => {
    if (!hasPublishedApp) {
      return (
        <div className={classes.sectionContainer}>
          <PageHeader
            description={translate('Branded_apps_banner_description')}
            heading={translate('Your_branded_mobile_apps')}
            icon={<PhoneIphoneIcon fontSize="large" className={classes.brandedAppsIcon} />}
          />
        </div>
      );
    }
    return (
      <>
        <Typography variant="subtitle1">
          <Translate id="Overview" />
        </Typography>
        <Grid className={classes.sectionContainer} container spacing={3} direction="row">
          <Grid className={classes.gridItem} item xs={12} sm={6}>
            <AppDownloadsCard
              isLoading={mobileAppStatisticsQuery.isLoading || mobileAppStatisticsQuery.isFetching}
              downloads={mobileAppStatisticsQuery.data?.Data[0].NumberDownload}
              translate={translate}
            />
          </Grid>
          <Grid className={classes.gridItem} item xs={12} sm={6}>
            <DashboardCard title={translate('Increase_your_revenue') as string}>
              <div className={classes.revenueTextContainer}>
                <Typography className={classes.revenueText} variant="body1">
                  <Translate id="Increase_revenue_description" />
                </Typography>
              </div>
            </DashboardCard>
          </Grid>
        </Grid>
      </>
    );
  };

  //Will be replaced by mobile apps page when fully released
  const renderLegacyPage = () => {
    if (hasAndroidApp || hasIosApp) {
      return <LegacyMobileSalesChannelsConfig />;
    }
    return <Discovery translate={translate} isMobileAppsOn={isMobileAppsOn} />;
  };

  const renderMobileAppsPage = () => {
    const statusArray = getSubmissionDetailsQuery.data?.Data?.Status || [];
    if (getSubmissionDetailsQuery.isError) {
      return (
        <FailedResponseMessage
          description="MobileApps_appDetails_responseErrorMessage"
          onRetry={() => getSubmissionDetailsQuery.refetch()}
          title="Error_fetching_app_details"
        />
      );
    }
    //show discovery page if no app details present
    if (
      !getSubmissionDetailsQuery.isFetching &&
      getSubmissionDetailsQuery.data?.Data?.MobileAppsSubmissionId === 0
    ) {
      return <Discovery translate={translate} isMobileAppsOn={isMobileAppsOn} />;
    }
    return (
      <>
        {renderOverview()}
        <Typography variant="subtitle1">
          <Translate id="Platform_settings" />
        </Typography>
        <Grid className={classes.sectionContainer} container direction="row" spacing={3}>
          <Grid className={classes.gridItem} item xs={12} sm={6}>
            <PlatformSettingCard
              appId={appId}
              isFlipdishStaff={isFlipdishStaff}
              loading={getSubmissionDetailsQuery.isLoading || getSubmissionDetailsQuery.isFetching}
              onError={handleDisplayError}
              status={
                statusArray[0]?.UpdateStatus || MobileAppsSubmissionStatus.UpdateStatusEnum.None
              }
              statusId={statusArray[0]?.MobileAppsSubmissionStatusId}
              submissionId={getSubmissionDetailsQuery.data?.Data.MobileAppsSubmissionId}
              type={MobileAppsSubmissionStatus.TypeEnum.Android}
            />
          </Grid>
          <Grid className={classes.gridItem} item xs={12} sm={6}>
            <PlatformSettingCard
              appId={appId}
              isFlipdishStaff={isFlipdishStaff}
              onError={() => {}}
              status={MobileAppsSubmissionStatus.UpdateStatusEnum.None}
              type={MobileAppsSubmissionStatus.TypeEnum.IOS}
            />
          </Grid>
        </Grid>
        <Typography variant="subtitle1">
          <Translate id="Mobile_app_details" />
        </Typography>
        <AppDetails
          description={getSubmissionDetailsQuery.data?.Data.AppDescription as string}
          isLoading={getSubmissionDetailsQuery.isLoading || getSubmissionDetailsQuery.isFetching}
          keywords={getSubmissionDetailsQuery.data?.Data.Keywords || []}
          logo={getSubmissionDetailsQuery.data?.Data.AppLogoUrl}
          shortDescription={getSubmissionDetailsQuery.data?.Data.AppShortDescription as string}
          title={getSubmissionDetailsQuery.data?.Data.AppName as string}
          translate={translate}
        />
        {hasPublishedApp && (
          <div className={classes.sectionContainer}>
            <Typography variant="subtitle1">
              <Translate id="General_settings" />
            </Typography>
            <GeneralSettings
              appId={appId}
              canEdit={hasEditSalesChannelsPermission}
              translate={translate}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <PageLayout documentTitle="Mobile_apps" title={<Translate id="Mobile_apps" />}>
      <Permissions allowed={['ViewSalesChannels', 'EditSalesChannels']}>
        <div className={classes.container}>
          {isMobileAppsOn ? renderMobileAppsPage() : renderLegacyPage()}
        </div>
      </Permissions>
    </PageLayout>
  );
};

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  notifyError: (data: NotifyProps) => dispatch(notifyError(data)),
});

type MappedState = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => {
  const { currentApp } = state;
  const getPermissionsSelector = permissionsSelector.hasPermissionFactory(['EditSalesChannels']);
  const getFlipdisherPermissionsSelector = permissionsSelector.hasPermissionFactory([
    'FlipdishStaff',
  ]);
  return {
    appId: currentApp.AppId as string,
    hasEditSalesChannelsPermission: getPermissionsSelector(state),
    hasIosApp: currentApp.HasIosApp,
    hasAndroidApp: currentApp.HasAndroidApp,
    isFlipdishStaff: getFlipdisherPermissionsSelector(state),
    isMobileAppsOn: flagService.isFlagOn(state, 'mobileApps'),
    translate: getTranslate(state.locale),
  };
};

const EnhancedComponent = connect(mapStateToProps, mapDispatchToProps)(MobileApps);
export { EnhancedComponent as MobileApps };
